import { OptionallyVisible } from 'components';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';

import { APIError } from 'types/error';
import { NotificationSeverity } from 'types/notifications';
import { CryptoAsset, RefundStatus } from 'types/pwcOrder';

import { formatDateLocal } from 'utils/date';
import { capitalizeFirstLetter } from 'utils/string';

import { KEYS, LABELS } from '../../keys';
import { OrderPwcSectionProps } from '../../types';
import { Item, ItemName, ItemValue, OnChainTransactionHash, SectionWrapper } from '../styles';
import { CancelRefundModal } from './components/cancelRefundModal/cancelRefundModal';
import { CurrencyIcon, CurrencyWrapper } from './styles';

const CANCEL_AVAILABLE_STATUSES = [RefundStatus.AWAITING_FUNDS, RefundStatus.INITIATED, RefundStatus.PENDING];

export const RefundSection = ({
  orderData,
  rowId,
  cancelRefund,
}: OrderPwcSectionProps & {
  cancelRefund: {
    trigger: (orderByUuid: string, refundUuid: string) => Promise<{ error?: APIError }>;
    isLoading: boolean;
  };
}) => {
  const { t } = useTranslation();
  const { pushToast } = useNotifications();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const refund = orderData?.refunds.find((refund) => refund.uuid === rowId);
  const cryptoAsset = refund?.cryptoAsset as CryptoAsset;

  const openCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const cancel = async () => {
    if (refund) {
      const response = await cancelRefund.trigger(refund.orderUuid, refund.uuid);
      closeCancelModal();
      if ('error' in response) {
        pushToast({
          severity: NotificationSeverity.error,
          message: t(LABELS.SECTIONS.CANCEL_REFUND.ERROR_MESSAGE),
        });
      }
    }
  };

  return (
    <Panel label={t(LABELS.SECTIONS.REFUND.TITLE)}>
      <CancelRefundModal
        isCanceling={cancelRefund.isLoading}
        isOpen={isCancelModalOpen}
        onClose={closeCancelModal}
        cancel={cancel}
        refund={refund}
      />
      <SectionWrapper>
        <Item>
          <ItemName>{t(LABELS.SECTIONS.REFUND.CRYPTOCURRENCY)}</ItemName>
          <CurrencyWrapper>
            <OptionallyVisible visible={Boolean(cryptoAsset?.iconUrl)}>
              <CurrencyIcon src={cryptoAsset?.iconUrl} alt={t(LABELS.SECTIONS.REFUND.CRYPTOCURRENCY)} />
            </OptionallyVisible>
            <ItemValue>{cryptoAsset?.cryptoCurrencyCode || t(KEYS.DASH)}</ItemValue>
          </CurrencyWrapper>
        </Item>

        <Item>
          <ItemName>{t(LABELS.SECTIONS.REFUND.NETWORK)}</ItemName>
          <ItemValue>{cryptoAsset?.network ? capitalizeFirstLetter(cryptoAsset.network) : t(KEYS.DASH)}</ItemValue>
        </Item>

        <OptionallyVisible visible={Boolean(refund?.walletAddress)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND.WALLET_ADDRESS)}</ItemName>
            <ItemValue>{refund?.walletAddress}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.walletMemo)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND.WALLET_MEMO)}</ItemName>
            <ItemValue>{refund?.walletMemo}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.onChain?.transactionHash)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.PAYMENT.TRANSACTION_HASH)}</ItemName>
            <OptionallyVisible visible={Boolean(refund?.onChain?.explorerUrl)}>
              <OnChainTransactionHash displayFullHash>
                <a href={refund?.onChain?.explorerUrl} target="_blank">
                  {refund?.onChain?.transactionHash}
                </a>
              </OnChainTransactionHash>
            </OptionallyVisible>
          </Item>
        </OptionallyVisible>

        <Item>
          <ItemName>{t(LABELS.SECTIONS.REFUND.CREATION_TIME)}</ItemName>
          <ItemValue>{formatDateLocal(refund?.createdAt)}</ItemValue>
        </Item>

        <OptionallyVisible visible={Boolean(refund?.expiresAt)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND.EXPIRATION_TIME)}</ItemName>
            <ItemValue>{formatDateLocal(refund?.expiresAt)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.processedAt)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND.PROCESSING_TIME)}</ItemName>
            <ItemValue>{formatDateLocal(refund?.processedAt)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.externalRefundId)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND.EXTERNAL_REFUND_ID)}</ItemName>
            <ItemValue>{refund?.externalRefundId}</ItemValue>
          </Item>
        </OptionallyVisible>
        <OptionallyVisible visible={refund && CANCEL_AVAILABLE_STATUSES.includes(refund?.status)}>
          <Button secondary onClick={openCancelModal}>
            {t(LABELS.SECTIONS.CANCEL_REFUND.MODAL_TITLE)}
          </Button>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
